// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { Check, Copy } from 'lucide-react';

import { copyTextToClipboard } from '@/lib/clipboard';
import { cn } from '@/lib/utils';

export interface MonoValueProps extends React.HTMLAttributes<HTMLSpanElement> {
  copyable?: boolean;
  /**
   * Break long values across lines instead of truncating. For surfaces where
   * the full value must stay readable (the install wizard's DNS records and
   * URLs, which IT re-types elsewhere); catalog chips keep the truncate default.
   */
  wrap?: boolean;
  children: React.ReactNode;
}

export function MonoValue({ children, copyable, wrap, className, ...props }: MonoValueProps) {
  const [copied, setCopied] = React.useState(false);
  // Hold the active "reset to idle" timer so rapid repeat clicks don't let the
  // first timer fire and clear the second click's confirmation prematurely,
  // and so the timer can't keep a reference to an unmounted component.
  const resetTimerRef = React.useRef<number | null>(null);

  const clearResetTimer = React.useCallback(() => {
    if (resetTimerRef.current !== null) {
      window.clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
  }, []);

  React.useEffect(() => clearResetTimer, [clearResetTimer]);

  const handleCopy = React.useCallback(async () => {
    if (typeof children !== 'string' && typeof children !== 'number') return;
    if (!(await copyTextToClipboard(String(children)))) return;
    setCopied(true);
    clearResetTimer();
    resetTimerRef.current = window.setTimeout(() => {
      resetTimerRef.current = null;
      setCopied(false);
    }, 1500);
  }, [children, clearResetTimer]);

  // `truncate` is one utility, so a later whitespace class does not remove it,
  // and the stylesheet order can keep the clip. Drop it when the value wraps.
  const callerClass = wrap
    ? className?.replace(/\btruncate\b/g, '').replace(/\bmax-w-\[[^\]]+\]/g, '')
    : className;

  return (
    <span
      className={cn(
        // max-w-full: an inline-flex chip has intrinsic width, so without a
        // bound a long value overflows its container instead of truncating
        // or wrapping inside it.
        'inline-flex max-w-full items-center gap-1 rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[12px] text-foreground',
        callerClass,
        wrap && 'min-w-0 items-start whitespace-normal',
      )}
      {...props}
    >
      <span className={cn('min-w-0', wrap ? 'break-all whitespace-normal' : 'truncate')}>
        {children}
      </span>
      {copyable && (
        <>
          <button
            type="button"
            onClick={handleCopy}
            aria-label={copied ? 'Copied' : 'Copy to clipboard'}
            className="shrink-0 rounded text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
          <span className="sr-only" aria-live="polite">
            {copied ? 'Copied to clipboard' : ''}
          </span>
        </>
      )}
    </span>
  );
}
